import { useEffect, useRef, useState } from 'react';
import { useFieldStore } from '@/store/field.store';
import FieldDamageAssessments from '../../pages/Field/FieldDamageAssessments';
import FieldRoadClosures from '../../pages/Field/FieldRoadClosures';
import FieldPdaZones from '../../pages/Field/FieldPdaZones';
import FieldTasks from '../../pages/Field/FieldTasks';
import FieldSupplyRequests from '../../pages/Field/FieldSupplyRequests';
import FieldTeam from '../../pages/Field/FieldTeam';
import FieldForms from '../../pages/Field/FieldForms';

import FaIcon from '../common/FaIcon';
import TabButton from '../common/TabButton';
const DRAWER_HEIGHTS = {
  closed: '0%',
  half: '50%',
  full: '90%',
};

const TAB_LABELS = {
  damage: 'Damage',
  roads: 'Roads',
  zones: 'Zones',
  tasks: 'Tasks',
  supplies: 'Supplies',
  forms: 'Forms',
  team: 'Team',
};



export default function FieldDrawer() {
  const { drawerState, drawerTab, setDrawerState, setDrawerTab } = useFieldStore();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStart === null || !drawerRef.current) return;

    const currentTouch = e.touches[0].clientY;
    const diff = currentTouch - touchStart;

    // Only allow dragging down when at top of drawer content
    const drawerContent = drawerRef.current.querySelector('.drawer-content');
    if (drawerContent && drawerContent.scrollTop > 0 && diff < 0) {
      return; // Let content scroll instead
    }

    setCurrentTranslate(diff);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setTouchStart(null);

    const screenHeight = window.innerHeight;
    const threshold = screenHeight * 0.15; // 15% threshold

    // Determine next state based on current state and drag direction
    if (Math.abs(currentTranslate) < threshold) {
      // Snap back to current state
      setCurrentTranslate(0);
      return;
    }

    if (currentTranslate > 0) {
      // Dragging down
      if (drawerState === 'full') {
        setDrawerState('half');
      } else if (drawerState === 'half') {
        setDrawerState('closed');
      }
    } else {
      // Dragging up
      if (drawerState === 'closed') {
        setDrawerState('half');
      } else if (drawerState === 'half') {
        setDrawerState('full');
      }
    }

    setCurrentTranslate(0);
  };

  const handleBackdropClick = () => {
    if (drawerState === 'half' || drawerState === 'full') {
      setDrawerState('closed');
    }
  };

  // Close drawer on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (drawerState === 'half' || drawerState === 'full')) {
        setDrawerState('closed');
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [drawerState, setDrawerState]);

  if (drawerState === 'closed') {
    return null;
  }

  const height = DRAWER_HEIGHTS[drawerState];
  const showTabs = drawerState === 'half' || drawerState === 'full';
  const showBackdrop = drawerState === 'half' || drawerState === 'full';

  return (
    <>
      {/* Backdrop */}
      {showBackdrop && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          onClick={handleBackdropClick}
          style={{ top: 'calc(env(safe-area-inset-top) + 3rem)' }}
        />
      )}

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed left-0 right-0 bottom-0 bg-surface-elevated z-50 flex flex-col shadow-2xl transition-transform duration-300 ease-out"
        style={{
          height,
          transform: isDragging ? `translateY(${Math.max(0, currentTranslate)}px)` : 'none',
          paddingBottom: 'env(safe-area-inset-bottom)',
          willChange: isDragging ? 'transform' : 'auto',
        }}
        role={showTabs ? 'dialog' : undefined}
        aria-label={showTabs ? 'Field operations drawer' : undefined}
      >
        {/* Drag Handle */}
        <div
          className="flex-shrink-0 flex items-center justify-center py-2 cursor-grab active:cursor-grabbing touch-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          role="button"
          aria-label="Drag to expand or collapse"
        >
          <FaIcon icon="grip-vertical" className="h-6 w-6 text-gray-400" />
        </div>

        {/* Tab Selector */}
        {showTabs && (
          <div className="flex-shrink-0 flex overflow-x-auto border-b border-gray-200 dark:border-gray-800">
            {(Object.keys(TAB_LABELS) as Array<keyof typeof TAB_LABELS>).map((tab) => (
              <TabButton key={tab} active={drawerTab === tab} onClick={() => setDrawerTab(tab)}>
                {TAB_LABELS[tab]}
              </TabButton>
            ))}
          </div>
        )}

        {/* Content Area */}
        {showTabs && (
          <div className="flex-1 overflow-y-auto drawer-content">
            {drawerTab === 'damage' && <FieldDamageAssessments />}
            {drawerTab === 'roads' && <FieldRoadClosures />}
            {drawerTab === 'zones' && <FieldPdaZones />}
            {drawerTab === 'tasks' && <FieldTasks />}
            {drawerTab === 'supplies' && <FieldSupplyRequests />}
            {drawerTab === 'forms' && <FieldForms />}
            {drawerTab === 'team' && <FieldTeam />}
          </div>
        )}
      </div>
    </>
  );
}
